// https://xeokit.github.io/xeokit-bim-viewer/docs/class/src/BIMViewer.js~BIMViewer.html
import React, { useEffect, useRef, useState } from "react"
import { useSelector, useDispatch } from "react-redux"
import { setIfcViewMode } from "@/slices/chatSlice"
import tippy from "tippy.js"
// @ts-ignore
import { BIMViewer, LocaleService } from "@xeokit/xeokit-bim-viewer/dist/xeokit-bim-viewer.es.js"
// @ts-ignore
import { messages as localeMessages } from "@xeokit/xeokit-bim-viewer/dist/messages.js"

import "https://kit.fontawesome.com/d129036538.js"
import "tippy.js/dist/tippy.css"
import "@xeokit/xeokit-bim-viewer/dist/xeokit-bim-viewer.css"
import FullscreenIcon from "@mui/icons-material/Fullscreen"
import ClearIcon from "@mui/icons-material/Clear"
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit"
import VisibilityIcon from "@mui/icons-material/Visibility"
import { Box, IconButton, CircularProgress, Tooltip as MuiTooltip } from "@mui/material"

import { generatePath } from "react-router-dom"
import { RoutesPath } from "@/router/config"
import { CustomLink } from "@/components/CustomLink/CustomLink"
import Tooltip from "@mui/material/Tooltip"
import Typography from "@mui/material/Typography"
import { processFileName } from "@/pages/documents/Documents"
import { isFileId } from "@/utils/folderSelection"

import "./Viewer.css"
import { AxiosServer } from "@/components/IFCViewer/IFCAxiosServer"
import theme from "@/theme"
import { onDownload } from "@/utils/download"
import { t } from "i18next"
import { registerIfcViewerMount } from "@/utils/ifcViewerErrorScope"

interface IFCViewerProps {
    file_id?: string
    fileName?: string
    elementIds?: string[]
    handleClose?: () => void
    large?: boolean
}

function destroyBimViewerSafely(instance: BIMViewer | null) {
    if (!instance) {
        return
    }
    try {
        instance.unloadProject()
    } catch (error) {
        console.warn("Error during BIMViewer unloadProject:", error)
    }
    try {
        const scene = instance.viewer?.scene
        if (scene?.objects) {
            const allObjectIds = Object.keys(scene.objects)
            scene.setObjectsSelected(allObjectIds, false)
            scene.setObjectsHighlighted(allObjectIds, false)
        }
    } catch (error) {
        console.warn("Error clearing BIMViewer selection/highlight:", error)
    }
    try {
        instance.destroy()
    } catch (error) {
        console.warn("Error during BIMViewer destroy:", error)
    }
}

// Helper function for safe view adjustments
function safeViewFitOrReset(bimViewerInstance: BIMViewer | null, elementIds?: (string | number)[]) {
    console.log("[safeViewFitOrReset] Called with elementIds:", elementIds)
    if (!bimViewerInstance) {
        console.warn("[safeViewFitOrReset] BIMViewer instance is not available.")
        return
    }
    try {
        if (elementIds && elementIds.length > 0) {
            console.log(`[safeViewFitOrReset] Fitting view to ${elementIds.length} objects:`, elementIds)
            bimViewerInstance.viewFitObjects(elementIds, () => {
                console.log("[safeViewFitOrReset] View fit complete.")
            })
        } else {
            console.log("[safeViewFitOrReset] Resetting view.")
            bimViewerInstance.resetView(() => {
                console.log("[safeViewFitOrReset] View reset complete.")
            })
        }
    } catch (error) {
        console.warn("[safeViewFitOrReset] Error during view adjustment:", error)
    }
}

const EMPTY_ELEMENT_IDS: string[] = []

export function IFCViewer({
    file_id = "3",
    fileName,
    elementIds = EMPTY_ELEMENT_IDS,
    handleClose,
    large = true,
}: IFCViewerProps): JSX.Element {
    const server = useRef<AxiosServer | null>(null)
    const bimViewer = useRef<BIMViewer | null>(null)
    const viewerLoaded = useRef(false)
    const canvasElement = useRef<HTMLCanvasElement>(null)
    const explorerElement = useRef<HTMLDivElement>(null)
    const toolbarElement = useRef<HTMLDivElement>(null)
    const inspectorElement = useRef<HTMLDivElement>(null)
    const bcfViewpointsElement = useRef<HTMLDivElement>(null)
    const navCubeCanvasElement = useRef<HTMLCanvasElement>(null)
    const viewerElement = useRef<HTMLDivElement>(null)
    const fullscreenContainerRef = useRef<HTMLDivElement>(null)

    const isLoadingRef = useRef(false)
    const ifcViewerScopeUnregister = useRef<(() => void) | null>(null)

    const [isExplorerOpen, setIsExplorerOpen] = useState(false)
    const [isInspectorOpen, setIsInspectorOpen] = useState(false)
    const [isFullscreen, setIsFullscreen] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    // Track which elementIds are valid in the current scene
    const [validElementIds, setValidElementIds] = useState<string[]>([])
    // Track if we should show a warning about invalid elements
    const [showInvalidElementsWarning, setShowInvalidElementsWarning] = useState(false)
    // Track if the model is fully loaded
    const [isModelLoaded, setIsModelLoaded] = useState(false)
    // Redux: get and set IFC view mode
    const dispatch = useDispatch()
    const viewMode = useSelector((state: any) => state.chat.ifcViewMode) as "select" | "showOnly"

    console.log("IFCViewer component rendered with file_id:", file_id, "elementIds:", elementIds, `large is ${large}`)
    // if (String(elementIds).length < 5) {
    //     elementIds = []
    // }
    // elementIds = ["3ZGD7y6S5209$mGLl18RZR"]
    // elementIds = ["3ZGD7y6S5209$mGLi_sPll"]
    // elementIds = ["38NblWsDL1I8DljLvn67cw"]

    const toggleFullscreen = () => {
        const nextFullscreenState = !isFullscreen
        setIsFullscreen(nextFullscreenState)
    }

    useEffect(() => {
        if (
            !viewerLoaded.current &&
            canvasElement.current &&
            explorerElement.current &&
            toolbarElement.current &&
            inspectorElement.current &&
            bcfViewpointsElement.current &&
            navCubeCanvasElement.current &&
            viewerElement.current
        ) {
            viewerLoaded.current = true
            initializeViewer()
        }

        const preventScroll = (e: WheelEvent) => {
            if (canvasElement.current && e.target instanceof Element && canvasElement.current.contains(e.target)) {
                e.preventDefault()
            }
        }

        if (canvasElement.current) {
            window.addEventListener("wheel", preventScroll, { passive: false })
        }

        return () => {
            window.removeEventListener("wheel", preventScroll)
            ifcViewerScopeUnregister.current?.()
            ifcViewerScopeUnregister.current = null
            destroyBimViewerSafely(bimViewer.current)
            bimViewer.current = null
            viewerLoaded.current = false
        }
    }, [])

    // Poll for validElementIds after model is loaded, with logging for debugging
    useEffect(() => {
        console.log("[IFCViewer] useEffect: isModelLoaded=", isModelLoaded, "elementIds=", elementIds)
        let pollTimeout: NodeJS.Timeout | null = null
        let pollCount = 0
        const maxPolls = 20 // e.g. poll for up to 10 seconds (20 * 500ms)
        function pollForValidElementIds() {
            pollCount++
            const isLoaded = isModelLoaded
            const viewer = bimViewer.current?.viewer
            const scene = viewer?.scene
            const objects = scene?.objects
            console.log(`[IFCViewer] Poll #${pollCount}: isModelLoaded=${isLoaded}, elementIds=`, elementIds, "scene.objects count=", objects ? Object.keys(objects).length : 0)
            if (isLoaded && elementIds.length && objects) {
                const valid = elementIds.filter(id => objects[id])
                setValidElementIds(valid)
                console.log(`[IFCViewer] Found validElementIds:`, valid)
                if (valid.length > 0 || pollCount >= maxPolls) {
                    // Show warning if we reached max polls and no valid elements found
                    if (pollCount >= maxPolls && valid.length === 0 && elementIds.length > 0) {
                        setShowInvalidElementsWarning(true)
                        setTimeout(() => setShowInvalidElementsWarning(false), 3000)
                    }
                    return // stop polling if found or max reached
                }
            } else {
                setValidElementIds(prev => (prev.length === 0 ? prev : []))
            }
            if (pollCount < maxPolls) {
                pollTimeout = setTimeout(pollForValidElementIds, 500)
            }
        }

        if (isModelLoaded && elementIds.length) {
            console.log("[IFCViewer] Starting pollForValidElementIds")
            pollForValidElementIds()
        } else {
            setValidElementIds(prev => (prev.length === 0 ? prev : []))
        }
        return () => {
            if (pollTimeout) clearTimeout(pollTimeout)
        }
    }, [isModelLoaded, elementIds])

    // Only reload the model when file_id changes
    useEffect(() => {
        console.log(`IFCViewer loadProject effect triggered with file_id: ${file_id}`)
        if (bimViewer.current && file_id) {
            if (isLoadingRef.current) {
                console.log("Skipping loadProject because a project is already loading")
                return
            }
            bimViewer.current.unloadProject()
            loadProject(file_id)
        }
    }, [file_id])

    // When elementIds or viewMode changes, apply the correct function to the current model (no reload)
    useEffect(() => {
        console.log(`IFCViewer effect triggered with elementIds: ${elementIds}, viewMode: ${viewMode}`)
        if (bimViewer.current && elementIds.length) {
            if (isLoadingRef.current) {
                console.log("Skipping selection/showOnly because a project is currently loading")
                return
            }
            if (viewMode === "select") {
                // Show all objects and select the relevant ones
                const viewer = bimViewer.current.viewer
                const scene = viewer.scene
                if (scene && scene.objects) {
                    scene.setObjectsVisible(Object.keys(scene.objects), true)
                    selectElementById(elementIds)
                }
            } else if (viewMode === "showOnly") {
                // Hide all objects except the selected ones and select them
                showOnlyElementIds(elementIds)
                safeViewFitOrReset(bimViewer.current, elementIds)
            }
        }
    }, [elementIds, viewMode])

    useEffect(() => {
        const handleEscKey = (event: KeyboardEvent) => {
            if (event.key === "Escape" && isFullscreen) {
                toggleFullscreen()
            }
        }
        document.addEventListener("keydown", handleEscKey)
        return () => {
            document.removeEventListener("keydown", handleEscKey)
        }
    }, [isFullscreen])

    function initializeViewer() {
        if (
            !canvasElement.current ||
            !explorerElement.current ||
            !toolbarElement.current ||
            !inspectorElement.current ||
            !bcfViewpointsElement.current ||
            !navCubeCanvasElement.current ||
            !viewerElement.current
        ) {
            console.warn("initializeViewer called before all required elements are available.")
            return
        }

        server.current = new AxiosServer({
            bimPath: "/bim",
            fileId: file_id,
        })

        bimViewer.current = new BIMViewer(server.current, {
            localeService: new LocaleService({ messages: localeMessages, locale: "en" }),
            canvasElement: canvasElement.current,
            keyboardEventsElement: document,
            explorerElement: explorerElement.current,
            toolbarElement: toolbarElement.current,
            inspectorElement: inspectorElement.current,
            bcfViewpointsElement: bcfViewpointsElement.current,
            navCubeCanvasElement: navCubeCanvasElement.current,
            busyModelBackdropElement: viewerElement.current,
            enableEditModels: true,
        })

        if (!ifcViewerScopeUnregister.current) {
            ifcViewerScopeUnregister.current = registerIfcViewerMount()
        }

        // Configure camera controls to be less sensitive. some ifc files are extremely sensitive
        if (bimViewer.current) {
            const cameraControl = bimViewer.current.viewer.cameraControl
            cameraControl.keyboardRotationRate = 45
            cameraControl.keyboardDollyRate = 7.5
            cameraControl.keyboardPanRate = 2.5
            cameraControl.mouseWheelDollyRate = 50
        }

        bimViewer.current.setConfigs({
            showSpaces: false,
            selectedGlowThrough: true,
            highlightGlowThrough: true,
            dtxEnabled: true,
        })

        bimViewer.current.localeService.on("updated", () => {
            const localizedElements = document.querySelectorAll(".xeokit-i18n")
            localizedElements.forEach((localizedElement: any) => {
                if (localizedElement.dataset.xeokitI18n) {
                    localizedElement.innerText = bimViewer.current!.localeService.translate(
                        localizedElement.dataset.xeokitI18n
                    )
                }
                const tipKey = localizedElement.dataset.xeokitI18ntip
                if (tipKey) {
                    const tipContent = bimViewer.current!.localeService.translate(tipKey)
                    if (tipContent && typeof tipContent === "string") {
                        localizedElement.dataset.tippyContent = tipContent
                        if (localizedElement._tippy) {
                            localizedElement._tippy.setContent(tipContent)
                        } else {
                            tippy(localizedElement, {
                                appendTo: "parent",
                                zIndex: 1000000,
                                allowHTML: true,
                            })
                        }
                    } else {
                        tippy(localizedElement, {
                            appendTo: "parent",
                        })
                    }
                }
            })
        })

        bimViewer.current.on("openExplorer", () => setIsExplorerOpen(true))
        bimViewer.current.on("closeExplorer", () => setIsExplorerOpen(false))

        bimViewer.current.on("openInspector", () => setIsInspectorOpen(true))
        bimViewer.current.on("closeInspector", () => setIsInspectorOpen(false))
        bimViewer.current.on("openMeasurements", () => setIsInspectorOpen(true))
        bimViewer.current.on("closeMeasurements", () => setIsInspectorOpen(false))
        bimViewer.current.on("openSections", () => setIsInspectorOpen(true))
        bimViewer.current.on("closeSections", () => setIsInspectorOpen(false))
        bimViewer.current.on("openComments", () => setIsInspectorOpen(true))
        bimViewer.current.on("closeComments", () => setIsInspectorOpen(false))

        bimViewer.current.on("openBCFViewpoints", () => {})
        bimViewer.current.on("closeBCFViewpoints", () => {})

        // Listen for model/project loaded event to set isModelLoaded
        // Add logs for all possible events
        bimViewer.current.on("projectLoaded", () => {
            console.log("[IFCViewer] Event: projectLoaded")
            setIsModelLoaded(true)
        })
        bimViewer.current.on("modelLoaded", () => {
            console.log("[IFCViewer] Event: modelLoaded")
            setIsModelLoaded(true)

            const scene = bimViewer.current.viewer.scene
            if (scene) {
                const aabb = scene.aabb // [xmin, ymin, zmin, xmax, ymax, zmax]
                const modelSize = Math.sqrt(
                    Math.pow(aabb[3] - aabb[0], 2) + Math.pow(aabb[4] - aabb[1], 2) + Math.pow(aabb[5] - aabb[2], 2)
                )

                const camera = bimViewer.current.viewer.camera
                const cameraControl = bimViewer.current.viewer.cameraControl

                if (modelSize < 1) {
                    camera.perspective.near = 0.001
                    camera.perspective.far = 10
                    cameraControl.mouseWheelDollyRate = 3
                } else if (modelSize < 10) {
                    camera.perspective.near = 0.01
                    camera.perspective.far = 100
                    cameraControl.mouseWheelDollyRate = 15
                } else if (modelSize < 100) {
                    camera.perspective.near = 0.1
                    camera.perspective.far = 1000
                    cameraControl.mouseWheelDollyRate = 30
                } else if (modelSize < 1000) {
                    camera.perspective.near = 0.5
                    camera.perspective.far = 10000
                    cameraControl.mouseWheelDollyRate = 60
                } else {
                    camera.perspective.near = 1.0
                    camera.perspective.far = 50000
                    cameraControl.mouseWheelDollyRate = 100
                }

                // set other rates similarly
            }
        })
        bimViewer.current.on("loaded", () => {
            console.log("[IFCViewer] Event: loaded")
            setIsModelLoaded(true)
        })
        // Reset isModelLoaded to false when loading starts
        bimViewer.current.on("projectLoading", () => {
            console.log("[IFCViewer] Event: projectLoading")
            setIsModelLoaded(false)
        })
        bimViewer.current.on("modelLoading", () => {
            console.log("[IFCViewer] Event: modelLoading")
            setIsModelLoaded(false)
        })
        bimViewer.current.on("loading", () => {
            console.log("[IFCViewer] Event: loading")
            setIsModelLoaded(false)
        })
    }

    function selectElementById(elementIds: (string | number)[], attempts = 0, maxAttempts = 10) {
        if (!bimViewer.current) {
            console.warn("BIMViewer instance is not initialized")
            return
        }
        console.log(`[selectElementById] Starting selection for IDs:`, elementIds)
        console.log(`[selectElementById] Current camera position:`, bimViewer.current.viewer.camera.eye)

        const viewer = bimViewer.current.viewer
        const scene = viewer.scene

        const sceneObjectIds = scene?.objects ? Object.keys(scene.objects) : []
        if (!scene || !scene.objects || sceneObjectIds.length === 0) {
            if (attempts < maxAttempts) {
                console.warn(
                    `[selectElementById] Attempt ${attempts + 1}/${maxAttempts}: Scene not ready (${sceneObjectIds.length} objects)`
                )
                setTimeout(() => selectElementById(elementIds, attempts + 1, maxAttempts), 1500)
            } else {
                console.warn(`[selectElementById] Failed to access scene.objects after ${maxAttempts} attempts`)
            }
            return
        }

        console.log(`[selectElementById] Scene has ${sceneObjectIds.length} objects. First 10:`, sceneObjectIds.slice(0, 10))
        console.log(`[selectElementById] Looking for elementIds:`, elementIds)
        const validIds = elementIds.filter(id => scene.objects[id])
        console.log(`[selectElementById] Matched ${validIds.length}/${elementIds.length} IDs. Valid:`, validIds)
        // Try to select a storey if any of the IDs correspond to storeys
        for (const id of elementIds) {
            try {
                // Suppress all logs from selectStorey, including success
                try {
                    bimViewer.current.selectStorey(id)
                } catch {}
            } catch (e) {
                // selectStorey logs error internally if not a storey, so ignore
            }
        }
        if (validIds.length === 0) {
            console.warn(`[selectElementById] No objects found with given IDs. ElementIds were:`, elementIds)
            if (scene && scene.objects) {
                const allObjectIds = Object.keys(scene.objects)
                scene.setObjectsVisible(allObjectIds, true)
                scene.setObjectsSelected(allObjectIds, false)
                console.log("[selectElementById] Showing all objects and unselecting due to no valid IDs")
                console.log("[selectElementById] Pre-reset camera:", viewer.camera.eye)
                setShowInvalidElementsWarning(true)
                setTimeout(() => setShowInvalidElementsWarning(false), 3000)
                console.log("[selectElementById] Performing view reset")
                safeViewFitOrReset(bimViewer.current)
            }
            return
        }

        const objectsToSelect = validIds.map(id => scene.objects[id].id)

        // Deselect all before selecting new elements
        if (scene && scene.objects) {
            const allObjectIds = Object.keys(scene.objects)
            console.log(`[selectElementById] Deselecting all ${allObjectIds.length} objects`)
            viewer.scene.setObjectsSelected(allObjectIds, false)
        }
        console.log(`[selectElementById] Selecting ${objectsToSelect.length} valid objects`)
        viewer.scene.setObjectsSelected(objectsToSelect, true)

        if (validIds.length === 1) {
            console.log(`[selectElementById] Flying to single object ${validIds[0]}`)
            console.log(`[selectElementById] Pre-fly camera:`, viewer.camera.eye)
            try {
                bimViewer.current.flyToObject(validIds[0])
            } catch (error) {
                console.warn(`[selectElementById] Error flying to object ${validIds[0]}:`, error)
            }
            console.log(`[selectElementById] Post-fly camera:`, viewer.camera.eye)
        } else if (validIds.length > 1) {
            console.log(`[selectElementById] Fitting view to ${validIds.length} objects`)
            console.log(`[selectElementById] Pre-fit camera:`, viewer.camera.eye)
            safeViewFitOrReset(bimViewer.current, validIds)
            console.log(`[selectElementById] Post-fit camera:`, viewer.camera.eye)
        }
    }

    function showOnlyElementIds(elementIds: (string | number)[], attempts = 0, maxAttempts = 10) {
        if (!bimViewer.current) {
            console.warn("[showOnlyElementIds] BIMViewer instance is not initialized")
            return
        }
        if (!elementIds.length) {
            console.warn("[showOnlyElementIds] No element IDs provided")
            return
        }
        console.log(`[showOnlyElementIds] Starting showOnly for IDs:`, elementIds)
        console.log(`[showOnlyElementIds] Current camera position:`, bimViewer.current.viewer.camera.eye)

        const viewer = bimViewer.current.viewer
        const scene = viewer.scene

        const sceneObjectCount = scene?.objects ? Object.keys(scene.objects).length : 0
        if (!scene || !scene.objects || sceneObjectCount === 0) {
            if (attempts < maxAttempts) {
                console.warn(
                    `[showOnlyElementIds] Attempt ${attempts + 1}/${maxAttempts}: Scene not ready (${sceneObjectCount} objects)`
                )
                setTimeout(() => showOnlyElementIds(elementIds, attempts + 1, maxAttempts), 1500)
            } else {
                console.warn(`[showOnlyElementIds] Failed to access scene.objects after ${maxAttempts} attempts`)
            }
            return
        }

        // Check which of our IDs are valid
        const validIds = elementIds.filter(id => scene.objects[id])
        // Try to select a storey if any of the IDs correspond to storeys
        for (const id of elementIds) {
            try {
                // Suppress all logs from selectStorey, including success
                try {
                    bimViewer.current.selectStorey(id, () => {
                        console.log(`Storey ${id} selected`)
                    })
                } catch {}
            } catch (e) {
                // selectStorey logs error internally if not a storey, so ignore
            }
        }
        if (validIds.length === 0) {
            console.warn(`No valid objects found with given IDs, skipping hide/show operation`)
            // Show all and unselect all
            if (scene && scene.objects) {
                const allObjectIds = Object.keys(scene.objects)
                scene.setObjectsVisible(allObjectIds, true)
                scene.setObjectsSelected(allObjectIds, false)
                console.log("[showOnlyElementIds] Showing all objects and unselecting due to no valid IDs")
                console.log("[showOnlyElementIds] Pre-reset camera:", viewer.camera.eye)
                setShowInvalidElementsWarning(true)
                setTimeout(() => setShowInvalidElementsWarning(false), 3000)
                console.log("[showOnlyElementIds] Performing view reset")
                safeViewFitOrReset(bimViewer.current)
            }
            return
        }

        const allObjectIds = Object.keys(scene.objects)
        console.log(`[showOnlyElementIds] Hiding all ${allObjectIds.length} objects`)
        scene.setObjectsVisible(allObjectIds, false)

        console.log(`[showOnlyElementIds] Showing ${validIds.length} valid objects`)
        scene.setObjectsVisible(validIds, true)

        // If only one element is specified, fly to it
        if (validIds.length === 1) {
            console.log(`[showOnlyElementIds] Flying to single object ${validIds[0]}`)
            console.log(`[showOnlyElementIds] Pre-fly camera:`, viewer.camera.eye)
            console.log(`[showOnlyElementIds] Object position:`, scene.objects[validIds[0]].aabb)
            viewer.cameraFlight.flyTo(scene.objects[validIds[0]])
        }
    }

    // Updated to accept optional mode argument
    function loadProject(projectId: string, retry = false, mode?: "select" | "showOnly") {
        if (!bimViewer.current) {
            console.warn("loadProject called but bimViewer.current is null.")
            return
        }

        if (isLoadingRef.current) {
            console.log(`Project ${projectId} load request ignored: another project is currently loading.`)
            return
        }

        console.log(`Loading project: ${projectId}`)
        isLoadingRef.current = true
        setIsLoading(true)

        bimViewer.current.unloadProject()

        bimViewer.current.loadProject(
            projectId,
            () => {
                console.log(`Project ${projectId} loaded successfully.`)
                setIsLoading(false)
                isLoadingRef.current = false
                if (elementIds.length) {
                    // Use the mode param if provided, else current viewMode
                    const modeToApply = mode || viewMode
                    if (modeToApply === "select") {
                        // Show all objects and select the relevant ones
                        const viewer = bimViewer.current.viewer
                        const scene = viewer.scene
                        if (scene && scene.objects) {
                            scene.setObjectsVisible(Object.keys(scene.objects), true)
                            selectElementById(elementIds)
                        }
                    } else if (modeToApply === "showOnly") {
                        // Hide all objects except the selected ones and select them
                        showOnlyElementIds(elementIds)
                        safeViewFitOrReset(bimViewer.current, elementIds)
                    }
                }
            },
            (errorMsg: string) => {
                console.warn(`Load project error for ${projectId}:`, errorMsg)
                setIsLoading(false)
                isLoadingRef.current = false

                if (!retry) {
                    console.log(`Retrying loadProject for ${projectId} after failure`)
                    bimViewer.current.unloadProject()
                    console.log(`Retrying loadProject for ${projectId}...`)
                    loadProject(projectId, true, mode)
                } else {
                    console.warn(`Retry for project ${projectId} also failed.`)
                }
            }
        )
    }

    useEffect(() => {
        const element = inspectorElement.current
        if (!element) return

        let isTransitioning = false

        const handleTransitionStart = (event: TransitionEvent) => {
            if (event.propertyName === "width" && event.target === element) {
                isTransitioning = true
            }
        }
        const handleTransitionEnd = (event: TransitionEvent) => {
            if (event.propertyName === "width" && event.target === element && isTransitioning) {
                isTransitioning = false
                console.log("Inspector transition ended.")
            }
        }

        element.addEventListener("transitionstart", handleTransitionStart)
        element.addEventListener("transitionend", handleTransitionEnd)

        return () => {
            element.removeEventListener("transitionstart", handleTransitionStart)
            element.removeEventListener("transitionend", handleTransitionEnd)
        }
    }, [])

    return (
        <Box
            ref={fullscreenContainerRef}
            className="ifc-viewer-container"
            sx={{
                display: "flex",
                boxSizing: "border-box",
                border: "1px solid #ddd",
                ...(isFullscreen
                    ? {
                          position: "fixed",
                          top: 0,
                          left: 0,
                          width: "100vw",
                          height: "calc(100vh - 50px)",
                          zIndex: 1500,
                          overflow: "hidden",
                          borderRadius: "4px",
                          backgroundColor: theme.palette.background.default,
                      }
                    : {
                          flex: 1,
                          position: "relative",
                          height: "100%",
                          overflow: "hidden",
                          boxShadow: "2px 2px 10px 0 rgba(0, 0, 0, 0.25)",
                          borderRadius: "4px",
                          backgroundColor: theme.palette.background.default,
                      }),
                // position: "relative",
            }}
        >
            <Box
                ref={explorerElement}
                className="xeokit-tabs xeokit-explorer"
                sx={{
                    width: isExplorerOpen ? 400 : 0,
                    height: "100%",
                    transition: "width 0.3s ease",
                    overflow: "hidden",
                    borderRight: isExplorerOpen ? "1px solid #ddd" : "none",
                    backgroundColor: "#f5f5f5",
                    flexShrink: 0,
                    display: "flex",
                    flexDirection: "column",
                }}
            />
            <Box
                sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    overflow: "hidden",
                }}
            >
                <Box
                    sx={{
                        borderBottom: "1px solid #ccc",
                        pl: 1.5,
                        pr: 1.5,
                        pt: 0.5,
                        pb: 0.5,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        backgroundColor: "#f5f5f5",
                        flexShrink: 0,
                    }}
                >
                    {/* Left group: xeokit toolbar + explorer + visibility */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Box
                            ref={toolbarElement}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                            }}
                        />
                        <IconButton
                            onClick={() => setIsExplorerOpen(!isExplorerOpen)}
                            size="small"
                            sx={{
                                zIndex: 3,
                                color: "primary.main",
                                backgroundColor: "rgba(255, 255, 255, 0.6)",
                                "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.9)" },
                                border: "1px solid rgba(0,0,0,0.1)",
                            }}
                            title={isExplorerOpen ? "Hide Explorer" : "Show Explorer"}
                        >
                            <i className="fas fa-sitemap"></i>
                        </IconButton>

                        {handleClose && isModelLoaded && validElementIds.length > 0 && (
                            <>
                                <MuiTooltip
                                    title={viewMode === "select" ? "Switch to Show Only Mode" : "Switch to Select Mode"}
                                >
                                    <IconButton
                                        onClick={() => {
                                            const newMode = viewMode === "select" ? "showOnly" : "select"
                                            dispatch(setIfcViewMode(newMode))
                                        }}
                                        size="small"
                                        sx={{
                                            zIndex: 3,
                                            color: viewMode === "select" ? "primary.main" : "secondary.main",
                                            backgroundColor: "rgba(255, 255, 255, 0.6)",
                                            "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.9)" },
                                            border: "1px solid rgba(0,0,0,0.1)",
                                        }}
                                    >
                                        <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                </MuiTooltip>
                            </>
                        )}
                    </Box>

                    {/* Center: filename */}
                    {handleClose && file_id && isFileId(file_id) && (
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                            <CustomLink
                                underline="none"
                                color="inherit"
                                to={generatePath(RoutesPath.DocumentDetails, {
                                    id: file_id,
                                    name: fileName || file_id,
                                })}
                                style={{
                                    maxWidth: "200px",
                                    lineHeight: "1.2",
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    cursor: "pointer",
                                }}
                            >
                                <Tooltip title={`${fileName || file_id || ""}`} placement="top">
                                    <Typography
                                        variant="inherit"
                                        fontSize={14}
                                        sx={{
                                            transition: "color 0.2s",
                                            "&:hover": { color: "primary.main" },
                                        }}
                                    >
                                        {processFileName(fileName || file_id || "")}
                                    </Typography>
                                </Tooltip>
                            </CustomLink>
                        </Box>
                    )}

                    {/* Right group: inspector + download + fullscreen + close */}
                    <Box gap={1} sx={{ display: "flex", alignItems: "center" }}>
                        <IconButton
                            onClick={() => setIsInspectorOpen(!isInspectorOpen)}
                            size="small"
                            sx={{
                                zIndex: 3,
                                color: "primary.main",
                                backgroundColor: "rgba(255, 255, 255, 0.6)",
                                "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.9)" },
                                border: "1px solid rgba(0,0,0,0.1)",
                            }}
                            title={isInspectorOpen ? "Hide Properties" : "Show Properties"}
                        >
                            <i className="fas fa-info-circle"></i>
                        </IconButton>

                        {file_id && (
                            <IconButton
                                onClick={() =>
                                    onDownload({
                                        path: fileName || "download",
                                        fileId: file_id,
                                        downloadSource: true,
                                        confirm: confirm,
                                    })
                                }
                                size="small"
                                sx={{
                                    zIndex: 3,
                                    color: "primary.main",
                                    backgroundColor: "rgba(255, 255, 255, 0.6)",
                                    "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.9)" },
                                    border: "1px solid rgba(0,0,0,0.1)",
                                }}
                                title={"Download the file"}
                            >
                                <i className="fas fa-download"></i>
                            </IconButton>
                        )}

                        <IconButton
                            onClick={toggleFullscreen}
                            size="small"
                            sx={{
                                zIndex: 3,
                                color: "primary.main",
                                backgroundColor: "rgba(255, 255, 255, 0.6)",
                                "&:hover": {
                                    backgroundColor: "rgba(255, 255, 255, 0.9)",
                                },
                                border: "1px solid rgba(0,0,0,0.1)",
                            }}
                            title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
                        >
                            {isFullscreen ? (
                                <FullscreenExitIcon fontSize="small" />
                            ) : (
                                <FullscreenIcon fontSize="small" />
                            )}
                        </IconButton>

                        {handleClose && (
                            <IconButton
                                onClick={handleClose}
                                color="error"
                                size="small"
                                sx={{
                                    backgroundColor: "rgba(255, 255, 255, 0.6)",
                                    "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.9)" },
                                    border: "1px solid rgba(0,0,0,0.1)",
                                }}
                                aria-label={t("close")}
                            >
                                <ClearIcon fontSize="small" />
                            </IconButton>
                        )}
                    </Box>
                </Box>
                <Box
                    ref={viewerElement}
                    sx={{
                        flex: 1,
                        position: "relative",
                        overflow: "hidden",
                    }}
                >
                    <canvas
                        ref={canvasElement}
                        style={{
                            width: "100%",
                            height: "100%",
                            display: "block",
                            pointerEvents: isLoading ? "none" : "auto",
                        }}
                    />
                    {showInvalidElementsWarning && (
                        <Box
                            sx={{
                                position: "absolute",
                                top: "20px",
                                left: 0,
                                right: 0,
                                display: "flex",
                                justifyContent: "center",
                                zIndex: 11,
                            }}
                        >
                            <Box
                                sx={{
                                    backgroundColor: "#8C9DB3",
                                    color: "white",
                                    padding: "8px 16px",
                                    borderRadius: "4px",
                                    boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
                                }}
                            >
                                <Typography variant="body2">{t("No selection found. Showing full model")}</Typography>
                            </Box>
                        </Box>
                    )}
                    <canvas
                        ref={navCubeCanvasElement}
                        style={{
                            position: "absolute",
                            top: "10px",
                            right: "70px",
                            width: "150px",
                            height: "150px",
                            zIndex: 2,
                        }}
                    />
                    {isLoading && (
                        <Box
                            sx={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: "rgba(255, 255, 255, 0.7)",
                                zIndex: 10,
                            }}
                        >
                            <CircularProgress />
                        </Box>
                    )}
                </Box>
            </Box>
            <Box
                ref={inspectorElement}
                className="xeokit-tabs xeokit-inspector"
                sx={{
                    width: isInspectorOpen ? 450 : 0,
                    height: "100%",
                    transition: "width 0.3s ease",
                    overflowX: "hidden",
                    overflowY: "auto",
                    borderLeft: isInspectorOpen ? "1px solid #ddd" : "none",
                    backgroundColor: "#f5f5f5",
                    flexShrink: 0,
                    display: "flex",
                    flexDirection: "column",
                }}
            />
            <Box
                ref={bcfViewpointsElement}
                className="xeokit-tabs xeokit-bcf"
                sx={{
                    height: "100%",
                    overflow: "hidden",
                    borderLeft: "1px solid #ddd",
                    backgroundColor: "#f5f5f5",
                    flexShrink: 0,
                    display: "flex",
                    flexDirection: "column",
                }}
            />
        </Box>
    )
}

function elementIdsEqual(prev?: string[], next?: string[]): boolean {
    const a = prev ?? EMPTY_ELEMENT_IDS
    const b = next ?? EMPTY_ELEMENT_IDS
    if (a.length !== b.length) {
        return false
    }
    return a.every((id, index) => id === b[index])
}

function propsAreEqual(prevProps: IFCViewerProps, nextProps: IFCViewerProps) {
    return (
        prevProps.file_id === nextProps.file_id &&
        prevProps.handleClose === nextProps.handleClose &&
        prevProps.large === nextProps.large &&
        prevProps.fileName === nextProps.fileName &&
        elementIdsEqual(prevProps.elementIds, nextProps.elementIds)
    )
}

interface IFCViewerErrorBoundaryProps {
    children: React.ReactNode
    resetKey: string
}

interface IFCViewerErrorBoundaryState {
    hasError: boolean
}

class IFCViewerErrorBoundary extends React.Component<IFCViewerErrorBoundaryProps, IFCViewerErrorBoundaryState> {
    state: IFCViewerErrorBoundaryState = { hasError: false }

    static getDerivedStateFromError(): Partial<IFCViewerErrorBoundaryState> {
        return { hasError: true }
    }

    componentDidUpdate(prevProps: IFCViewerErrorBoundaryProps) {
        if (prevProps.resetKey !== this.props.resetKey && this.state.hasError) {
            this.setState({ hasError: false })
        }
    }

    componentDidCatch(error: Error) {
        console.warn("IFC viewer render error:", error)
    }

    render() {
        if (this.state.hasError) {
            return (
                <Box sx={{ p: 2, color: "text.secondary" }}>
                    <Typography variant="body2">{t("3D viewer could not be displayed.")}</Typography>
                </Box>
            )
        }
        return this.props.children
    }
}

const MemoizedIFCViewer = React.memo(IFCViewer, propsAreEqual)

function IFCViewerSafe(props: IFCViewerProps) {
    const resetKey = `${props.file_id ?? ""}:${(props.elementIds ?? []).join(",")}`
    return (
        <IFCViewerErrorBoundary resetKey={resetKey}>
            <MemoizedIFCViewer key={resetKey} {...props} />
        </IFCViewerErrorBoundary>
    )
}

export default IFCViewerSafe
